import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, map } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  MANAGE_MENU_DEFAULT_PHASE,
  MANAGE_MENU_PHASE_OPTIONS,
  createManageMenuFormValue,
} from './manage-menu.data';
import {
  ManageMenuFormValue,
  ManageMenuItem,
  ManageMenuPagination,
  ManageMenuPhaseOption,
  ManageMenuTreeItem,
} from './manage-menu.interfaces';

type ManageMenuQueryValue = string | number | null | undefined;
type ManageMenuQueryParams = Record<string, ManageMenuQueryValue>;
type ManageMenuSortField = 'menu_name' | 'menu_code';
type ManageMenuSortOrder = 'asc' | 'desc';

@Component({
  selector: 'app-manage-menu-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-menu.html',
  styleUrl: './manage-menu.scss',
})
export class ManageMenuPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly menuForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected menus: ManageMenuItem[] = [];
  protected parentMenus: ManageMenuItem[] = [];
  protected menuTree: ManageMenuTreeItem[] = [];
  protected visibleMenuRows: ManageMenuTreeItem[] = [];
  protected expandedMenuIds = new Set<number>();
  protected currentSearchTerm = '';
  protected pagination: ManageMenuPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };
  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected menuModalOpen = false;
  protected deleteModalOpen = false;
  protected modalMode: 'create' | 'edit' | 'view' = 'create';
  protected selectedMenu: ManageMenuItem | null = null;
  protected isLoading = false;
  protected formSubmitted = false;
  protected sortBy: ManageMenuSortField | null = null;
  protected sortOrder: ManageMenuSortOrder = 'asc';

  protected readonly filterForm: UntypedFormGroup;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.menuForm = this.fb.group({
      menu_name: ['', [Validators.required, Validators.maxLength(120)]],
      menu_url: ['', [Validators.maxLength(255)]],
      menu_code: ['', [Validators.required, Validators.maxLength(100)]],
      order_by: [null, [Validators.min(0)]],
      phase: [null],
      icon_class: ['fa-bars', [Validators.required, Validators.maxLength(120)]],
      parent_menu_id: [null],
    });
  }

  ngOnInit(): void {
    this.loadMenuPage(1);

    this.filterForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(250),
      )
      .subscribe(() => {
        this.loadMenuPage(1);
      });
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.loadMenuPage(1);
  }

  protected openAddMenuModal(): void {
    this.modalMode = 'create';
    this.selectedMenu = null;
    this.menuForm.enable({ emitEvent: false });
    this.menuForm.reset(createManageMenuFormValue());
    this.formSubmitted = false;
    this.menuModalOpen = true;
    this.loadParentMenuOptions();
  }

  protected openEditMenuModal(menu: ManageMenuItem): void {
    this.modalMode = 'edit';
    this.selectedMenu = menu;
    this.menuForm.enable({ emitEvent: false });
    this.menuForm.reset({
      menu_name: menu.menu_name,
      menu_url: menu.menu_url,
      menu_code: menu.menu_code,
      order_by: menu.order_by,
      phase: menu.phase ?? MANAGE_MENU_DEFAULT_PHASE,
      icon_class: menu.icon_class,
      parent_menu_id: menu.parent_menu_id,
    });
    this.formSubmitted = false;
    this.menuModalOpen = true;
    this.loadParentMenuOptions();
  }

  protected openViewMenuModal(menu: ManageMenuItem): void {
    this.modalMode = 'view';
    this.selectedMenu = menu;
    this.menuForm.enable({ emitEvent: false });
    this.menuForm.reset({
      menu_name: menu.menu_name,
      menu_url: menu.menu_url,
      menu_code: menu.menu_code,
      order_by: menu.order_by,
      phase: menu.phase ?? MANAGE_MENU_DEFAULT_PHASE,
      icon_class: menu.icon_class,
      parent_menu_id: menu.parent_menu_id,
    });
    this.formSubmitted = false;
    this.menuModalOpen = true;
    this.menuForm.disable({ emitEvent: false });
    this.loadParentMenuOptions();
  }

  protected openDeleteMenuModal(menu: ManageMenuItem): void {
    this.selectedMenu = menu;
    this.deleteModalOpen = true;
  }

  protected closeMenuModal(): void {
    this.menuModalOpen = false;
    this.selectedMenu = null;
    this.modalMode = 'create';
    this.menuForm.enable({ emitEvent: false });
    this.menuForm.reset(createManageMenuFormValue());
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedMenu = null;
  }

  protected saveMenu(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.menuForm.invalid) {
      this.formSubmitted = true;
      this.menuForm.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload();
    const request$ =
      this.modalMode === 'create'
        ? this.httpSvc.requestCall(AuthEndPoints.ADD_MENU, ApiMethod.POST, payload)
        : this.httpSvc.requestCall(AuthEndPoints.EDIT_MENU, ApiMethod.PATCH, payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
          this.closeMenuModal();
          this.parentMenus = [];
          this.loadMenuPage(this.pagination.page_no);
      },
    });
  }

  protected confirmDelete(): void {
    if (!this.selectedMenu) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_MENU, ApiMethod.DELETE, { id: this.selectedMenu.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.parentMenus = [];
          this.loadMenuPage(this.pagination.page_no);
        },
      });
  }

  protected trackById(_: number, item: ManageMenuItem): number {
    return item.id;
  }

  protected trackByTreeId(_: number, item: ManageMenuTreeItem): number {
    return item.id;
  }

  protected isRootParent(menu: ManageMenuItem): boolean {
    return menu.parent_menu_id === null;
  }

  protected getParentMenuName(parentMenuId: number | null): string {
    if (parentMenuId === null) {
      return '-';
    }

    return this.menus.find((menu) => menu.id === parentMenuId)?.menu_name ?? '-';
  }

  protected getVisibleParentMenus(): ManageMenuItem[] {
    if (!this.selectedMenu) {
      return this.parentMenus;
    }

    return this.parentMenus.filter((menu) => menu.id !== this.selectedMenu?.id);
  }

  protected isActiveIcon(iconClass: string | null): boolean {
    return Boolean(iconClass?.trim());
  }

  protected getPhaseOptions(): readonly ManageMenuPhaseOption[] {
    return MANAGE_MENU_PHASE_OPTIONS;
  }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.menuForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected onPhaseChange(menu: ManageMenuItem, phaseValue: string): void {
    const nextPhase = this.resolvePhaseValue(phaseValue);
    if (menu.phase === nextPhase) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.EDIT_MENU, ApiMethod.PATCH, { id: menu.id, phase: nextPhase })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.reloadMenus();
        },
        error: () => {
          this.reloadMenus();
        },
      });
  }

  private loadParentMenuOptions(): void {
    this.fetchMenus(AuthEndPoints.GET_MENUS).subscribe({
      next: (menuRows) => {
        this.parentMenus = [...menuRows];
      },
      error: () => {
        this.parentMenus = [];
      },
    });
  }

  private loadMenuPage(pageNo = this.pagination.page_no): void {
    const search = this.getSearchValue();
    this.currentSearchTerm = search;

    const query = this.buildQueryString({
      search,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.isLoading = true;
    this.httpSvc.requestCall(`${AuthEndPoints.GET_MENUS}${query}`, ApiMethod.GET, '').pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.finishLoading();
      }),
    ).subscribe({
      next: (response) => {
        const menuRows = this.extractMenus(this.resolveMenuRows(response));
        this.menus = menuRows;
        this.setVisibleMenuRows(menuRows, Boolean(search));
        this.pagination = this.extractPagination(response) ?? this.pagination;
      },
      error: () => {
        this.visibleMenuRows = [];
        this.menuTree = [];
        this.pagination = {
          page_no: pageNo,
          page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
          total_records: 0,
          total_pages: 0,
        };
      },
    });
  }

  private reloadMenus(): void {
    this.loadMenuPage(this.pagination.page_no);
  }

  private fetchMenus(apiRoute: string) {
    this.isLoading = true;

    return this.httpSvc.requestCall(apiRoute, ApiMethod.GET, '').pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.finishLoading();
      }),
      map((response) => this.extractMenus(this.resolveMenuRows(response))),
    );
  }

  private buildPayload(): ManageMenuFormValue {
    const formValue = this.menuForm.getRawValue();
    const menuUrl = String(formValue.menu_url ?? '').trim();
    const phase = this.resolvePhaseValue(formValue.phase);

    const payload: ManageMenuFormValue = {
      menu_name: String(formValue.menu_name ?? '').trim(),
      menu_url: menuUrl || null,
      menu_code: String(formValue.menu_code ?? '').trim(),
      order_by: this.normalizeOptionalNumber(formValue.order_by),
      icon_class: String(formValue.icon_class ?? '').trim(),
      parent_menu_id: formValue.parent_menu_id === '' || formValue.parent_menu_id === null ? null : Number(formValue.parent_menu_id),
      phase: phase,
    };

    if (this.modalMode === 'edit' && this.selectedMenu) {
      payload.id = this.selectedMenu.id;
    }

    return payload;
  }

  private normalizeOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private extractMenus(rawMenus: unknown): ManageMenuItem[] {
    if (!Array.isArray(rawMenus)) {
      return [];
    }

    return rawMenus.map((item: Record<string, unknown>) => ({
      id: Number(item['id']),
      serial_no: Number(item['serial_no'] ?? 0),
      is_read: Boolean(item['is_read']),
      is_write: Boolean(item['is_write']),
      menu_url: typeof item['menu_url'] === 'string' ? item['menu_url'] : null,
      order_by: item['order_by'] === null || item['order_by'] === undefined ? null : Number(item['order_by']),
      phase: item['phase'] === null || item['phase'] === undefined ? null : Number(item['phase']),
      menu_code: typeof item['menu_code'] === 'string' ? item['menu_code'] : null,
      menu_name: typeof item['menu_name'] === 'string' ? item['menu_name'] : null,
      icon_class: typeof item['icon_class'] === 'string' ? item['icon_class'] : null,
      parent_menu_id: item['parent_menu_id'] === null || item['parent_menu_id'] === undefined ? null : Number(item['parent_menu_id']),
    }));
  }

  private setVisibleMenuRows(rawMenus: ManageMenuItem[], expandAll = false): void {
    this.menuTree = this.buildMenuTree(rawMenus);
    this.visibleMenuRows = this.flattenVisibleMenuTree(this.menuTree);
    this.expandedMenuIds = expandAll ? new Set(this.collectExpandableMenuIds(this.menuTree)) : new Set<number>();
  }

  private buildMenuTree(rawMenus: ManageMenuItem[]): ManageMenuTreeItem[] {
    const sortedMenus = [...rawMenus].sort((left, right) => {
      const leftParent = left.parent_menu_id ?? Number.MAX_SAFE_INTEGER;
      const rightParent = right.parent_menu_id ?? Number.MAX_SAFE_INTEGER;
      if (leftParent !== rightParent) {
        return leftParent - rightParent;
      }

      const leftOrder = left.order_by ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.order_by ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return (left.menu_name ?? '').localeCompare(right.menu_name ?? '');
    });

    const nodeMap = new Map<number, ManageMenuTreeItem>();
    sortedMenus.forEach((menu) => {
      nodeMap.set(menu.id, {
        ...menu,
        depth: 0,
        children: [],
      });
    });

    const roots: ManageMenuTreeItem[] = [];
    sortedMenus.forEach((menu) => {
      const node = nodeMap.get(menu.id);
      if (!node) {
        return;
      }

      const parentId = menu.parent_menu_id;
      if (parentId === null || !nodeMap.has(parentId)) {
        roots.push(node);
        return;
      }

      const parent = nodeMap.get(parentId);
      if (!parent) {
        roots.push(node);
        return;
      }

      node.depth = parent.depth + 1;
      parent.children.push(node);
    });

    const sortChildren = (items: ManageMenuTreeItem[]): ManageMenuTreeItem[] => {
      const sortedItems = [...items].sort((left, right) => {
        const leftOrder = left.order_by ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.order_by ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return (left.menu_name ?? '').localeCompare(right.menu_name ?? '');
      });

      return sortedItems.map((item) => ({
        ...item,
        children: sortChildren(item.children),
      }));
    };

    const assignSerialNumbers = (items: ManageMenuTreeItem[], isRootLevel: boolean, rootCounterRef: { value: number }): ManageMenuTreeItem[] => {
      let siblingCounter = 0;

      return items.map((item) => {
        const nextItem: ManageMenuTreeItem = {
          ...item,
          children: assignSerialNumbers(item.children, false, rootCounterRef),
        };

        if (isRootLevel) {
          rootCounterRef.value += 1;
          nextItem.display_serial_no = rootCounterRef.value;
          nextItem.child_index = undefined;
        } else {
          siblingCounter += 1;
          nextItem.child_index = siblingCounter;
        }

        return nextItem;
      });
    };

    const rootCounterRef = { value: 0 };
    return assignSerialNumbers(sortChildren(roots), true, rootCounterRef);
  }

  private flattenVisibleMenuTree(tree: ManageMenuTreeItem[]): ManageMenuTreeItem[] {
    const rows: ManageMenuTreeItem[] = [];

    const visit = (items: ManageMenuTreeItem[]): void => {
      items.forEach((item) => {
        rows.push(item);
        if (item.children.length > 0 && this.expandedMenuIds.has(item.id)) {
          visit(item.children);
        }
      });
    };

    visit(tree);
    return rows;
  }

  protected toggleMenuGroup(menu: ManageMenuTreeItem): void {
    if (!menu.children.length) {
      return;
    }

    const nextExpanded = new Set(this.expandedMenuIds);
    if (nextExpanded.has(menu.id)) {
      nextExpanded.delete(menu.id);
    } else {
      nextExpanded.add(menu.id);
    }

    this.expandedMenuIds = nextExpanded;
    this.visibleMenuRows = this.flattenVisibleMenuTree(this.menuTree);
  }

  protected toggleSort(field: ManageMenuSortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadMenuPage(1);
  }

  protected isSortAscending(field: ManageMenuSortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageMenuSortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected isExpanded(menu: ManageMenuTreeItem): boolean {
    return this.expandedMenuIds.has(menu.id);
  }

  protected isChildRow(menu: ManageMenuTreeItem): boolean {
    return menu.depth > 0;
  }

  protected hasChildren(menu: ManageMenuTreeItem): boolean {
    return menu.children.length > 0;
  }

  protected getMenuPadding(menu: ManageMenuTreeItem): Record<string, string> {
    return {
      '--menu-depth': String(menu.depth),
    };
  }

  protected getMenuChildrenCount(menu: ManageMenuTreeItem): number {
    return menu.children.length;
  }

  private getSearchValue(): string {
    return (this.filterForm.get('search')?.value ?? '').toString().trim();
  }

  private collectExpandableMenuIds(tree: ManageMenuTreeItem[]): number[] {
    const expandableIds: number[] = [];

    const visit = (items: ManageMenuTreeItem[]): void => {
      items.forEach((item) => {
        if (item.children.length > 0) {
          expandableIds.push(item.id);
          visit(item.children);
        }
      });
    };

    visit(tree);
    return expandableIds;
  }

  private resolveMenuRows(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return [];
    }

    const value = (response as { value?: unknown }).value;
    if (!value || typeof value !== 'object') {
      return [];
    }

    const nestedMenus = (value as { menus?: unknown }).menus;
    if (Array.isArray(nestedMenus)) {
      return nestedMenus;
    }

    if (Array.isArray(value)) {
      return value;
    }

    const nestedValue = (value as { value?: unknown }).value;
    if (nestedValue && typeof nestedValue === 'object') {
      const nestedValueMenus = (nestedValue as { menus?: unknown }).menus;
      if (Array.isArray(nestedValueMenus)) {
        return nestedValueMenus;
      }
    }

    return [];
  }

  private extractPagination(response: unknown): ManageMenuPagination | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const value = (response as { value?: unknown }).value;
    if (!value || typeof value !== 'object') {
      return null;
    }

    const pagination = (value as { pagination?: unknown }).pagination;
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }

    const record = pagination as Record<string, unknown>;
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? 10),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private buildQueryString(params: ManageMenuQueryParams): string {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === 'all') {
        return;
      }
      queryParams.set(key, String(value));
    });
    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return 'No records';
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `Showing ${start} to ${end} of ${this.pagination.total_records} results`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (!totalPages) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages: number[] = [];
    const addPage = (pageNo: number): void => {
      if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
        pages.push(pageNo);
      }
    };

    addPage(1);
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);
    if (windowStart > 2) pages.push(-1);
    for (let page = windowStart; page <= windowEnd; page += 1) addPage(page);
    if (windowEnd < totalPages - 1) pages.push(-1);
    addPage(totalPages);
    return pages;
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadMenuPage(pageNo);
  }

  private resolvePhaseValue(value: unknown): number {
    const normalizedValue = Number(value);
    if ([1, 2, 3].includes(normalizedValue)) {
      return normalizedValue;
    }

    return MANAGE_MENU_DEFAULT_PHASE;
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.cdr.detectChanges();
  }
}
